/**
 * ClientWhatsAppSessionCard — card di una SESSIONE di conversazione WhatsApp
 * nella cronologia cliente (vista 7, BETA). Stesso linguaggio delle card della
 * timeline (cardStyles): flat, iconcina gradiente, kicker, ora a filo destro.
 *
 * L'header mostra l'intervallo completo della sessione: "15:30 – 16:42" nello
 * stesso giorno, "ieri 22:41 – 08:12" quando attraversa la mezzanotte (la card
 * sta sotto la chip giorno dell'ULTIMO messaggio). Espansa: bolle compatte
 * stile chat (entrata a sinistra, uscita a destra con autore), ultime 20 con
 * "Mostra altri"; in piè "Apri in WhatsApp" verso il drawer.
 *
 * Design doc: MD/features/2026-08-10-drawer-note-ui-ux-redesign-proposta.md
 */

import React, { useState } from 'react';
import { ExternalLink } from 'lucide-react';
import type { ClientWhatsAppSession } from '../../../lib/services/clientWhatsAppService';
import { TimelineFooter } from './TimelineFooter';
import { formatSessionRange, sameCalendarDay, relativeDayLabel } from './sessionRange';
export { formatSessionRange } from './sessionRange';
import { CardMedia } from './CardMedia';
import { ConversationAvatar } from '../../messages/ConversationAvatar';
import { CHIP_NEUTRAL_CLS, formatTime } from './cardStyles';

/** Bolle mostrate di default nella vista espansa. */
const VISIBLE_BUBBLES = 20;

/** Badge "Beta" accanto al kicker: la vista WhatsApp in cronologia è in beta. */
const BETA_BADGE_CLS =
  'inline-flex items-center px-1.5 py-[1px] rounded-full text-[9px] font-semibold uppercase tracking-[0.05em] ' +
  'bg-[rgba(35,131,226,0.10)] text-[#2383E2] dark:bg-[#2383E2]/20 dark:text-[#5CA5E8] flex-shrink-0';


interface ClientWhatsAppSessionCardProps {
  session: ClientWhatsAppSession;
  formatDate: (dateString: string) => string;
}

export const ClientWhatsAppSessionCard: React.FC<ClientWhatsAppSessionCardProps> = ({
  session,
  formatDate,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const visibleMessages = showAll
    ? session.messages
    : session.messages.slice(-VISIBLE_BUBBLES);
  const hiddenCount = session.messages.length - visibleMessages.length;

  const openInWhatsApp = () => {
    // Il drawer e' sempre montato in Layout: gli si chiede il numero aziendale
    // di QUESTA sessione e la chat, e ci pensa lui a cambiare numero se serve e
    // a selezionare la chat quando la lista e' pronta (prima si selezionava
    // subito sul numero attivo, e la selezione veniva spazzata dal ricaricamento).
    void import('../../../lib/stores/whatsappDrawerStore').then(({ useWhatsAppDrawerStore }) => {
      useWhatsAppDrawerStore.getState().openWithChat(session.chatJid, session.configId, session.chatName);
    });
  };

  const endDate = new Date(session.endAt);
  const startDate = new Date(session.startAt);
  const crossesDays = !sameCalendarDay(startDate, endDate);

  return (
    <div className="group relative mb-3 rounded-lg border border-[rgba(55,53,47,0.16)] bg-white transition-colors hover:border-[rgba(55,53,47,0.26)] dark:border-[#3F4447] dark:bg-[#2F3437] dark:hover:border-[#565D61]">
      <div className="min-w-0 p-3">
        <button
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="block w-full text-left"
        >
          {/* Siamo gia' nella scheda del contatto: aprire col suo nome non dice
              niente. Apre con quello che e' stato detto; il nome resta solo per i
              gruppi, dove e' davvero l'informazione. */}
          <CardMedia
            media={
              <ConversationAvatar
                name={session.chatName}
                whatsappChatId={session.chatJid}
                isGroup={session.isGroup}
                size="sm"
              />
            }
          >
            <div
              className="truncate text-sm font-medium text-[#37352F] dark:text-white"
              title={session.isGroup || expanded ? session.chatName : session.lastPreview || session.chatName}
            >
              {session.isGroup || expanded ? session.chatName : session.lastPreview || session.chatName}
            </div>
            <div className="mt-0.5 truncate text-xs text-[#787774] dark:text-white/50">
              {session.isGroup
                ? session.lastPreview
                : `${session.messageCount} ${session.messageCount === 1 ? 'messaggio' : 'messaggi'}${
                    session.messageCount > 1 ? ` · ${session.inboundCount} ricevuti, ${session.outboundCount} inviati` : ''
                  }`}
            </div>
          </CardMedia>

          <TimelineFooter
            tipo="WhatsApp"
            accent="conversazione"
            orario={formatSessionRange(session.startAt, session.endAt)}
            titoloData={crossesDays ? `${formatDate(session.startAt)} → ${formatDate(session.endAt)}` : formatDate(session.endAt)}
            expanded={expanded}
            left={
              <>
                <span className={BETA_BADGE_CLS}>Beta</span>
                {session.chatNumber && session.chatNumber !== session.chatName && (
                  <span className={CHIP_NEUTRAL_CLS}>{session.chatNumber}</span>
                )}
                {session.configLabel && <span className={`${CHIP_NEUTRAL_CLS} max-w-[180px] truncate`}>Numero: {session.configLabel}</span>}
              </>
            }
          />
        </button>

        {expanded && (
          <>
            <div className="mt-2.5 flex flex-col gap-1.5">
              {visibleMessages.map((message) => {
                const isOut = message.direction === 'outbound';
                const timeLabel = crossesDays
                  ? `${relativeDayLabel(new Date(message.timestamp), new Date())} ${formatTime(message.timestamp)}`
                  : formatTime(message.timestamp);
                return (
                  <div
                    key={message.id}
                    className={`max-w-[78%] px-2.5 py-1.5 text-[12.5px] leading-snug text-[#37352F] dark:text-white/90 ${
                      isOut
                        ? 'self-end rounded-[10px_10px_3px_10px] bg-[rgba(35,131,226,0.10)] dark:bg-[#2383E2]/20'
                        : 'self-start rounded-[10px_10px_10px_3px] bg-[rgba(55,53,47,0.05)] dark:bg-white/[0.06]'
                    }`}
                  >
                    <span className="whitespace-pre-wrap break-words">{message.content}</span>
                    <div
                      className={`mt-0.5 text-[10px] text-[#9B9A97] dark:text-white/40 ${isOut ? 'text-right' : ''}`}
                    >
                      {isOut && message.senderName ? `${message.senderName} · ` : ''}
                      {timeLabel}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-2.5 flex items-center justify-between border-t border-[rgba(55,53,47,0.09)] pt-2 dark:border-[#3F4447]">
              {hiddenCount > 0 ? (
                <button
                  onClick={() => setShowAll(true)}
                  className="text-[11px] text-[#787774] hover:text-[#37352F] dark:text-white/50 dark:hover:text-white"
                >
                  Mostra altri {hiddenCount} messaggi
                </button>
              ) : (
                <span />
              )}
              <button
                onClick={openInWhatsApp}
                className="inline-flex items-center gap-1 text-[11px] text-[#787774] hover:text-[#37352F] dark:text-white/50 dark:hover:text-white"
              >
                <ExternalLink size={12} />
                Apri in WhatsApp
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
